import { AlertCircle, Loader2 } from "lucide-react";
import { memo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";

import type { IGlobalSearchServerStatusProps } from "./types";

// NOTE: The state of /search/global only, as one line under the results. A
// failure never replaces what the device already found — pages, settings,
// chats and the other client groups stay on screen above it.
const GlobalSearchServerStatusComponent = ({
  isLoading,
  isError,
  onRetry,
}: IGlobalSearchServerStatusProps): JSX.Element | null => {
  const { t } = useTranslation("globalSearch");

  if (isError) {
    return (
      <div
        role="status"
        className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-input px-3 py-2"
        data-testid="global-search-server-error"
      >
        <span className="flex items-center gap-2 text-sm text-foreground-secondary">
          <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
          {t("error")}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={onRetry}
          data-testid="global-search-server-retry"
        >
          {t("retry")}
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        role="status"
        className="mt-4 flex items-center justify-center gap-2 py-2 text-sm text-foreground-secondary"
        data-testid="global-search-server-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("loading")}
      </div>
    );
  }

  return null;
};

export const GlobalSearchServerStatus = memo(GlobalSearchServerStatusComponent);
